import { useState, useEffect } from 'react'
import { Dialog, Box, Typography, IconButton, Button, TextField } from '@mui/material'
import { Icon as Iconify } from '@iconify/react'
import CloseIcon from '@mui/icons-material/Close'
import { HexColorPicker } from 'react-colorful'
import { appColors } from 'utils'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'
import { isValidHex } from 'utils/colorFunctions'

const ColorPickerModal = ({ open, onClose, color = '#ffffff', onChange = null }) => {
  const { getCardBackground, getWindowBarColor, getTextColor } = useInnovaTheme()
  const [selectedColor, setSelectedColor] = useState(color)
  const [inputValue, setInputValue] = useState(color)

  useEffect(() => {
    if (!open) return
    setSelectedColor(color)
    setInputValue(color)
  }, [open, color])

  const handlePickerChange = (hex) => {
    setSelectedColor(hex)
    setInputValue(hex)
  }

  const handleInputChange = (e) => {
    let val = e.target.value
    if (val && !val.startsWith('#')) val = '#' + val
    setInputValue(val)
    if (isValidHex(val)) setSelectedColor(val)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableEscapeKeyDown={false}
      PaperProps={{
        sx: {
          width: 260,
          backgroundColor: getCardBackground(),
          color: getTextColor(),
          display: 'flex',
          flexDirection: 'column',
        },
      }}>
      <Box
        sx={{
          height: 44,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          gap: 1,
          color: appColors.headerTextColor,
          backgroundColor: getWindowBarColor(),
          borderBottom: '1px solid rgba(255,255,255,0.12)',
        }}>
        <Typography sx={{ fontWeight: 600, flex: 1, fontSize: 14 }}>Color Picker</Typography>
        <IconButton onClick={onClose} size='small'>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Box>

      <Box sx={{ px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
        <HexColorPicker color={selectedColor} onChange={handlePickerChange} style={{ width: '100%' }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              flexShrink: 0,
              borderRadius: 0.75,
              backgroundColor: isValidHex(inputValue) ? inputValue : selectedColor,
            }}
          />
          <TextField
            fullWidth
            value={inputValue}
            onChange={handleInputChange}
            error={!isValidHex(inputValue)}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'monospace',
                fontSize: 13,
              },
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          px: 2,
          py: 1,
          borderTop: '1px solid rgba(255,255,255,0.10)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          backgroundColor: getWindowBarColor(),
        }}>
        <Button onClick={onClose} sx={{ color: appColors.itemTextColor }} size='small'>
          Cancel
        </Button>
        {onChange !== null && (
          <Button
            variant='outlined'
            size='small'
            disabled={!isValidHex(inputValue)}
            onClick={() => {
              onChange(selectedColor)
              onClose()
            }}
            startIcon={<Iconify icon='material-symbols:save' width={18} />}>
            Apply
          </Button>
        )}
      </Box>
    </Dialog>
  )
}

export default ColorPickerModal
